/* eslint-disable require-yield */
import {
  PayloadAction,
  createAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit'
import { Task as SagaTask, eventChannel } from 'redux-saga'
import {
  all,
  call,
  cancel,
  cancelled,
  fork,
  put,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects'

import * as schema from '../schema'
import {
  FireEvent,
  FireEventType,
  FireSnapshot,
  getDatabase,
  getUserEmail,
  subToCustomer,
} from '../api'
import {
  Customer,
  InitialCustomer,
  createEmptyCustomer,
  validateCustomer,
} from '../schema'
import {
  EMPTY_ARRAY,
  DeepReadonly,
  ValueOf,
  createEventBatcher,
  log,
} from '../utils'

import { YieldReturn } from './common'

export const MAX_CUSTOMERS_AT_ONCE = 80

const PREFIX = 'customers'

type CustomersState = DeepReadonly<{
  byID: string[]
  currentLimit: number
  data: Record<string, Customer>
  /**
   * Children the list query returned that validateCustomer refused, so they
   * never reach byID. See selectLoadedQueryRows.
   */
  rejectedIDs: string[]
}>

const initialState: CustomersState = {
  byID: [],
  currentLimit: 20,
  data: {},
  rejectedIDs: [],
}

const customersSlice = createSlice({
  extraReducers(builder) {
    return builder.addCase('persist/REHYDRATE', (state) => {
      state.byID = []
      state.currentLimit = 20
      state.rejectedIDs = []
    })
  },
  name: PREFIX,
  initialState,
  reducers: {
    createdCustomer(
      { data, byID },
      {
        payload: { customer },
      }: PayloadAction<{
        customer: InitialCustomer
      }>,
    ) {
      byID.splice(0, 0, customer.firebaseKey)
      data[customer.firebaseKey] = createEmptyCustomer(customer)
    },
    receivedCustomersFirebaseEvents(
      state,
      { payload: { events } }: PayloadAction<{ events: FireEvent<Customer>[] }>,
    ) {
      const { data } = state

      // `state.byID` is an Immer draft, so every element read goes through a
      // Proxy trap. indexOf() over the draft therefore costs a trap per entry,
      // and this loop did up to two of them per event - once for the key and
      // once for prevKey. On the first load Firebase emits one child_added per
      // customer, which made list startup quadratic in proxy traps and is the
      // bulk of the "list takes too long to load" report.
      //
      // Copying out once, mutating a plain array, then assigning back keeps the
      // exact same ordering semantics while paying for the draft only twice.
      const ids = state.byID.slice()

      // Every branch below asks "where is this key, if anywhere". During the
      // initial load the answer is always "nowhere", but finding that out cost
      // a full scan per event. Membership is the only thing needed to rule the
      // scan out, so keep it in a Set and only scan when the key really is
      // present and its position matters.
      const present = new Set(ids)

      // Firebase streams the initial load in order, so prevKey is almost always
      // the key inserted immediately before. Remembering just that one position
      // turns the other lookup into a hit as well. Any removal invalidates it,
      // since a splice shifts everything after it.
      let lastKey = ''
      let lastIdx = -1

      // CAST on prevKey: if the value is null or undefined indexOf() returns
      // -1, so newIdx lands at 0 and the child goes to the front - which is
      // exactly Firebase's meaning for a null prevKey.
      const indexOfPrev = (prevKey: string | null | undefined): number =>
        prevKey != null && prevKey === lastKey && lastIdx > -1
          ? lastIdx
          : prevKey != null && !present.has(prevKey)
          ? -1
          : ids.indexOf(prevKey!)

      const indexOfKey = (key: string): number =>
        present.has(key) ? ids.indexOf(key) : -1

      const place = (key: string, prevKey: string | null | undefined): void => {
        const currIdx = indexOfKey(key)
        const newIdx = indexOfPrev(prevKey) + 1

        if (currIdx !== newIdx) {
          currIdx > -1 && ids.splice(currIdx, 1)
          ids.splice(newIdx, 0, key)
        }

        present.add(key)
        lastKey = key
        lastIdx = newIdx
      }

      events.forEach((event) => {
        const { key, type } = event

        if (type === 'child_added' || type === 'child_changed') {
          data[key] = event.item
          place(key, event.prevKey)
        }
        if (type === 'child_moved') {
          place(key, event.prevKey)
        }
        if (type === 'child_removed') {
          const index = indexOfKey(key)
          index > -1 && ids.splice(index, 1)

          present.delete(key)
          lastKey = ''
          lastIdx = -1

          delete data[event.key]
        }
      })

      state.byID = ids
    },
    rejectedCustomerRows(
      state,
      { payload: { keys } }: PayloadAction<{ keys: string[] }>,
    ) {
      for (const key of keys) {
        if (!state.rejectedIDs.includes(key)) state.rejectedIDs.push(key)
      }
    },
    receivedSimpleCustomers(
      { data },
      {
        payload: { customers },
      }: PayloadAction<{
        customers: readonly schema.SimpleCustomer[]
      }>,
    ) {
      for (const customer of customers) {
        const { objectID } = customer

        if (!data[objectID]) {
          data[objectID] = createEmptyCustomer({
            customerAddress: '',
            customerName: '',
            customerPhone: '',
            customerPhoneAlt: '',
            ecohomeRep: '',
            firebaseKey: objectID,
            solarCompany: '',
            solarRep: '',
          })
        }

        // CAST: Just created above
        Object.assign(data[objectID]!, customer)
      }
    },
    receivedSingleCustomerFirebaseEvents(
      state,
      {
        payload: { customerID, updates },
      }: PayloadAction<{
        customerID: string
        updates: CustomerChannelPayload
      }>,
    ) {
      const { data } = state

      if (!data[customerID]) {
        data[customerID] = createEmptyCustomer({
          customerAddress: '',
          customerName: '',
          customerPhone: '',
          customerPhoneAlt: '',
          ecohomeRep: '',
          firebaseKey: customerID,
          solarCompany: '',
          solarRep: '',
        })
      }

      //   let allDataUpdate = {}

      for (const { field, value } of updates) {
        //allDataUpdate = { ...allDataUpdate, [field]: value } as Customer
        if (field === 'owner_house') {
          continue
        }

        // @ts-expect-error Typescript edge case
        data[customerID]![field] = value
      }

      // let objToUpdateNote = {}

      // if (customer) {
      //   const { note: windowsNote, totalWindows } = generatedNewWindowsNotes({
      //     ...customer,
      //     ...(allDataUpdate as Customer),
      //   })
      //   const roofNotes = generatedRoofNotes({
      //     ...customer,
      //     ...(allDataUpdate as Customer),
      //   })
      //   const acNotes = generatedACNotes({
      //     ...customer,
      //     ...(allDataUpdate as Customer),
      //   })
      //   const { note: panelNotes, totalPanelCost } = generatedPanelNotes({
      //     ...customer,
      //     ...(allDataUpdate as Customer),
      //   })

      //   if (totalPanelCost !== customer!.panel_removal_cost) {
      //     objToUpdateNote = {
      //       panel_removal_cost: totalPanelCost,
      //       panel_removal_customer_cost: totalPanelCost,
      //     }
      //     data[customerID]!.panel_removal_cost = totalPanelCost
      //     data[customerID]!.panel_removal_customer_cost = totalPanelCost
      //   }

      //   if (totalWindows !== customer!.new_windows_quantity) {
      //     objToUpdateNote = {
      //       new_windows_quantity: totalWindows,
      //     }
      //     data[customerID]!.new_windows_quantity = totalWindows
      //   }

      //   if (windowsNote !== customer!.new_windows_notes) {
      //     objToUpdateNote = {
      //       new_windows_notes: windowsNote,
      //     }
      //     data[customerID]!.new_windows_notes = windowsNote
      //   }
      //   if (roofNotes !== customer!.roof_notes) {
      //     objToUpdateNote = {
      //       ...objToUpdateNote,
      //       roof_notes: roofNotes,
      //     }
      //     data[customerID]!.roof_notes = roofNotes
      //   }
      //   if (acNotes !== customer!.air_conditioner_notes) {
      //     objToUpdateNote = {
      //       ...objToUpdateNote,
      //       air_conditioner_notes: acNotes,
      //     }
      //     data[customerID]!.air_conditioner_notes = acNotes
      //   }
      //   if (panelNotes !== customer!.panel_removal_notes) {
      //     objToUpdateNote = {
      //       ...objToUpdateNote,
      //       panel_removal_notes: panelNotes,
      //     }
      //     data[customerID]!.panel_removal_notes = panelNotes
      //   }

      //   if (Object.keys(objToUpdateNote).length) {
      //     updateCustomer(customerID, objToUpdateNote)
      //   }
      // }
    },
    requestedLimitIncrease(customers) {
      if (customers.currentLimit === MAX_CUSTOMERS_AT_ONCE) {
        return
      }
      customers.currentLimit += 20
    },
    requestedLimitReset(customers) {
      customers.currentLimit = 20
    },
    /**
     * The list is meant to surface the customer you last opened, and it did -
     * eventually. Opening one wrote to Firebase, and the move only happened
     * once the server echoed that change back, with the event batcher's fixed
     * one second debounce stacked on top of the round trip. On a phone in the
     * field that read as five seconds of the list showing a stale order while
     * the rep hunted for the customer they had just been looking at.
     *
     * Nothing about that wait is load-bearing: the app already knows which
     * customer was opened at the moment it happens. Moving it locally right
     * then costs nothing and the server event, when it arrives, asks for the
     * same position and lands as a no-op.
     */
    openedCustomer(
      { byID, data },
      { payload: { customerID } }: PayloadAction<{ customerID: string }>,
    ) {
      const currIdx = byID.indexOf(customerID)

      if (currIdx === 0) {
        return
      }

      if (currIdx === -1) {
        // Outside the loaded page, which is the usual reason to search for
        // someone. A search hit already has its data (receivedSimpleCustomers),
        // so it can go on top now instead of after the server round trip plus
        // the batcher's debounce (13433629337). With no data behind it, it
        // would be a blank row: leave it to the subscription.
        const customer = data[customerID]
        if (!customer?.customerName) {
          return
        }
        // Same value touchCustomerSortKey writes, so the row does not read
        // "Updated 3 months ago" until the server event lands.
        customer.sort_key = -Date.now()
        byID.splice(0, 0, customerID)
        return
      }

      byID.splice(currIdx, 1)
      byID.splice(0, 0, customerID)
    },
  },
})

const {
  actions: {
    createdCustomer,
    openedCustomer,
    receivedCustomersFirebaseEvents,
    receivedSimpleCustomers,
    receivedSingleCustomerFirebaseEvents,
    rejectedCustomerRows,
    requestedLimitIncrease,
    requestedLimitReset,
  },
  reducer: customers,
} = customersSlice

export {
  createdCustomer,
  customers,
  openedCustomer,
  receivedCustomersFirebaseEvents,
  receivedSimpleCustomers,
  receivedSingleCustomerFirebaseEvents,
  rejectedCustomerRows,
}

export const subToCustomers = createAction(`${PREFIX}/subToCustomers`)

export const requestedMoreCustomers = createAction(
  `${PREFIX}/requestedMoreCustomers`,
)

/**
 * `keepLimit` is for paging, which rebuilds the subscription on purpose. Every
 * other unsubscribe (leaving the screen, pull to refresh) goes back to one page.
 */
export const unSubFromCustomers = createAction<
  { keepLimit: boolean } | undefined
>(`${PREFIX}/unSubFromCustomers`)

export const subToSingleCustomer = createAction<{ customerID: string }>(
  `${PREFIX}/subToSingleCustomer`,
)

export const unSubFromSingleCustomer = createAction<{ customerID: string }>(
  `${PREFIX}/unSubFromSingleCustomer`,
)

//#region selectors

interface GlobalState {
  [PREFIX]: CustomersState
}

/**
 * Whether the list should ask the query for another page.
 *
 * Both conditions are load-bearing.
 *
 * `loaded >= limit` is the one that was missing. requestedMoreCustomers does
 * not just widen the query - moreCustomersWatcher tears the Firebase
 * subscription down and builds a new one, and unSubFromCustomers separately
 * fires requestedLimitReset, so the page size fights itself. Doing that while
 * the first page is still arriving replays child_added for customers already in
 * byID and re-places each against a fresh prevKey chain, which loses the
 * sort_key order the server sent.
 *
 * FlatList calls onEndReached whenever the end of the content sits within
 * onEndReachedThreshold of the viewport, and a one-row list always does - so
 * without this check a single arrived customer was enough to trigger the churn,
 * with nobody scrolling.
 *
 * `limit < MAX` is the stop condition. It reads the limit, not the row count:
 * rows are not guaranteed to reach exactly MAX (13433629394).
 *
 * `loaded` must count what the QUERY returned (selectLoadedQueryRows), not the
 * rows on screen. Counting the rows on screen stopped the list for good at 19
 * of 20 (13433629394): see selectLoadedQueryRows.
 */
export const shouldRequestMorePage = (loaded: number, limit: number): boolean =>
  loaded > 0 && loaded >= limit && limit < MAX_CUSTOMERS_AT_ONCE

export const selectCurrentLimit = ({
  customers: { currentLimit },
}: GlobalState): number => currentLimit

/**
 * How many children the list query has delivered, whether or not they can be
 * shown. This is the number to compare against currentLimit, because a page
 * being complete is a fact about the query.
 *
 * The rows on screen are fewer: selectAllCustomers hides soft-deleted customers
 * and validateCustomer refuses malformed records, and both still use up a place
 * in limitToFirst(). `Customers/-MSAQJXqB7-5j3WQc2eG` (2021, 13 checklist
 * fields, no sort_key) is the live case: Firebase sorts a missing sort_key
 * first, so it holds slot 0 of every query and can never be shown. Comparing
 * rows on screen read every page as still arriving, so the list never asked for
 * another one and ended at 19 customers (Jaron's video, 13433629394).
 */
export const selectLoadedQueryRows = ({
  customers: { byID, rejectedIDs },
}: GlobalState): number => byID.length + rejectedIDs.length

const selectAllCustomersIDs = ({ customers: { byID } }: GlobalState) => {
  if (byID.length === 0) {
    return EMPTY_ARRAY as string[]
  }
  return byID
}

export const selectCustomer = (
  { customers: { data } }: GlobalState,
  customerID: string,
): Customer => {
  const customer = data[customerID]
  if (!customer) {
    return schema.createEmptyCustomer({
      firebaseKey: customerID,
    })
  }
  return customer
}

const selectAllCustomerData = ({ customers: { data } }: GlobalState) => data

export const selectAllCustomers: (state: GlobalState) => Customer[] =
  createSelector(
    selectAllCustomersIDs,
    selectAllCustomerData,
    (byID, data): Customer[] => {
      if (byID.length === 0) {
        return EMPTY_ARRAY as Customer[]
      }

      // A typical ID is 20 characters long
      return byID
        .filter((id) => id.length >= 18)
        .map((id) => {
          const customerOrUndef = data[id]

          if (customerOrUndef) {
            return customerOrUndef
          }

          log(
            `selectAllCustomers() -> Could not fetch customer data for id ${id}`,
          )

          return createEmptyCustomer({
            customerAddress: '',
            customerName: '',
            customerPhone: '',
            customerPhoneAlt: '',
            ecohomeRep: '',
            firebaseKey: id,
            solarCompany: '',
            solarRep: '',
          })
        })
        .filter((c) => !c.deleted)
    },
  )

export interface SelectCustomerParams {
  customerID: string
}

export const makeSelectCustomer = () =>
  createSelector(
    selectAllCustomerData,
    (_: GlobalState, args: SelectCustomerParams) => args,
    (customerData, { customerID }): Customer => {
      return (
        customerData[customerID] ||
        createEmptyCustomer({
          customerAddress: '',
          customerName: '',
          customerPhone: '',
          customerPhoneAlt: '',
          ecohomeRep: '',
          firebaseKey: customerID,
          solarCompany: '',
          solarRep: '',
        })
      )
    },
  )

export interface SelectCustomerFieldParams<
  K extends schema.CustomerField = schema.CustomerField,
> {
  customerID: string
  field: K
}

export const makeSelectCustomerField = <K extends schema.CustomerField>() =>
  createSelector(
    selectAllCustomerData,
    (_: GlobalState, args: SelectCustomerFieldParams<K>) => args,
    (customerData, { customerID, field }): Customer[K] => {
      const customer =
        customerData[customerID] ||
        createEmptyCustomer({
          customerAddress: '',
          customerName: '',
          customerPhone: '',
          customerPhoneAlt: '',
          ecohomeRep: '',
          firebaseKey: customerID,
          solarCompany: '',
          solarRep: '',
        })

      return customer[field]
    },
  )
//#endregion selectors

//#region customersChannel

type CustomersChannelPayload = FireEvent<Customer>[]

interface RejectedRow {
  rejectedKey: string
}

const createCustomersSubscription = (
  eventType: FireEventType,
  listener: (e: FireEvent<Customer> | Error) => void,
  onRejected?: (key: string) => void,
) =>
  function customersSubscription(data: FireSnapshot, prevKey?: string | null) {
    try {
      const { key } = data

      if (!key) {
        throw new ReferenceError(`${eventType} without key`)
      }

      // A typical ID is 20 characters long
      // if (key.length < 18) {
      //   throw new ReferenceError(`Customer key too short: ${key}`)
      // }

      if (eventType === 'child_added' || eventType === 'child_changed') {
        const val = data.val()

        const validation = validateCustomer(val as Record<string, unknown>)

        if (validation.ok) {
          listener({
            item: validation.sanitized,
            key,
            prevKey,
            type: eventType,
          })
        } else {
          onRejected?.(key)
          throw new Error(
            validation.err || JSON.stringify(validation.errMap, null, 4),
          )
        }
      }
      if (eventType === 'child_moved' || eventType === 'child_removed') {
        const val = data.val()

        const validation = validateCustomer(val as Record<string, unknown>)

        if (validation.ok) {
          listener({
            item: validation.sanitized,
            key,
            prevKey,
            type: eventType,
          })
        }
      }
      if (eventType === 'child_removed') {
        listener({
          item: schema.createEmptyCustomer({
            customerAddress: '',
            customerName: '',
            customerPhone: '',
            customerPhoneAlt: '',
            ecohomeRep: '',
            firebaseKey: '',
            solarCompany: '',
            solarRep: '',
          }),
          key,
          prevKey,
          type: eventType,
        })
      }
    } catch (e) {
      log(`Error inside customers subscription (${eventType}):`)
      log(e)
    }
  }

const createCustomersChannel = (limit: number) =>
  eventChannel<CustomersChannelPayload | RejectedRow | Error>((emit) => {
    const userEmail = getUserEmail()
    if (!userEmail) {
      throw new ReferenceError(
        `userEmail not defined in createCustomersChannel() -> ${userEmail}`,
      )
    }
    const isCyberCam = userEmail.endsWith('cybercamscctv.com')

    const dbRef = getDatabase()
      .ref(isCyberCam ? 'Customers-CyberCam' : 'Customers')
      .orderByChild('sort_key')
      .limitToFirst(limit)

    // 12800619368 set `leading: true` and `maxWait: 2000` here in 901adb97 to
    // put the first customer on screen the moment it landed. REVERTED
    // 2026-08-23: it broke the list order, and the order matters more than the
    // three seconds it bought.
    //
    // With a leading edge the first render is a ONE-ROW list. FlatList calls
    // onEndReached whenever the end of the content sits within
    // onEndReachedThreshold (0.3) of the viewport, which a one-row list always
    // does - so pagination fired with nobody scrolling. requestedMoreCustomers
    // does not widen the live query: moreCustomersWatcher tears the
    // subscription down and builds a new one, and unSubFromCustomers separately
    // fires requestedLimitReset. Mid-load that replays child_added for
    // customers already in byID, re-places each against a fresh prevKey chain,
    // and loses the sort_key order the server sent. The customer just opened
    // rendered first and alone, then dropped to a fixed position.
    //
    // shouldRequestMorePage() now guards that condition, which was wrong on its
    // own terms - it asked for another page from the very first customer. That
    // guard is the real fix and it stays. This flag is reverted as well, on
    // purpose, so the order cannot regress from this cause even if the guard
    // turns out not to be the whole story. Restore it only after the order is
    // confirmed on a device.
    //
    // The cold-start ticket does not depend on this flag anyway: the diagnostic
    // measured 6434ms of dead wait for Firebase to answer the query, which no
    // amount of client-side batching can touch. The remaining work there is
    // persistence.
    const eventBatcher = createEventBatcher<FireEvent<Customer>>({
      debounceTime: 1000,
    })

    eventBatcher.onEvents(emit)

    dbRef.on(
      'child_added',
      createCustomersSubscription(
        'child_added',
        (e) => {
          if (e instanceof Error) {
            eventBatcher.flush()
            emit(e)
          } else {
            eventBatcher.eventReceived(e)
          }
        },
        (rejectedKey) => {
          emit({ rejectedKey })
        },
      ),
    )

    dbRef.on(
      'child_changed',
      createCustomersSubscription('child_changed', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    dbRef.on(
      'child_moved',
      createCustomersSubscription('child_moved', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    dbRef.on(
      'child_removed',
      createCustomersSubscription('child_removed', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    return () => {
      eventBatcher.flush()
      eventBatcher.off()
      dbRef.off()
    }
  })

function* subToCustomersSaga() {
  const currentLimit = selectCurrentLimit(yield select())

  const customersChannel: YieldReturn<typeof createCustomersChannel> =
    yield call(createCustomersChannel, currentLimit)

  try {
    while (true) {
      const payload: CustomersChannelPayload | RejectedRow = yield take(
        customersChannel,
      )

      if ('rejectedKey' in payload) {
        yield put(rejectedCustomerRows({ keys: [payload.rejectedKey] }))
        continue
      }

      yield put(
        receivedCustomersFirebaseEvents({
          events: payload,
        }),
      )
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      customersChannel.close()
    }
  }
}

function* customersSubWatcher() {
  while (true) {
    yield take(subToCustomers)

    const customersSubTask: SagaTask = yield fork(subToCustomersSaga)

    const unSub: ReturnType<typeof unSubFromCustomers> = yield take(
      unSubFromCustomers,
    )

    yield cancel(customersSubTask)
    // Paging must not reset: redux-saga runs this put BEFORE the increase in
    // moreCustomersWatcher, so every page went reset -> +20 and the query could
    // never pass 40 (13433629394).
    if (!unSub.payload?.keepLimit) {
      yield put(requestedLimitReset())
    }
  }
}

function* moreCustomersWatcher() {
  // A check is also performed inside the reducer, but let's avoid unnecessary
  // updates
  // const currentLimit = selectCurrentLimit(yield select())
  // if (currentLimit !== MAX_CUSTOMERS_AT_ONCE) {
  // }
  yield put(unSubFromCustomers({ keepLimit: true }))
  yield put(requestedLimitIncrease())
  yield put(subToCustomers())
}

//#endregion customersChannel

//#region singleCustomer

interface CustomerUpdate {
  field: keyof Customer
  value: ValueOf<Customer>
}

type CustomerChannelPayload = CustomerUpdate[]

const createCustomerChannel = (customerID: string) =>
  eventChannel<CustomerChannelPayload>((emit) => {
    const eventBatcher = createEventBatcher<CustomerUpdate>({
      debounceTime: 500,
    })

    eventBatcher.onEvents(emit)

    const unSub = subToCustomer(customerID, (field, value) => {
      eventBatcher.eventReceived({
        field,
        value,
      })
    })

    return () => {
      eventBatcher.flush()
      eventBatcher.off()
      unSub()
    }
  })

function* subToCustomerSaga(customerID: string) {
  const customerChan: YieldReturn<typeof createCustomerChannel> = yield call(
    createCustomerChannel,
    customerID,
  )

  try {
    while (true) {
      const updates: CustomerChannelPayload = yield take(customerChan)

      yield put(
        receivedSingleCustomerFirebaseEvents({
          customerID,
          updates,
        }),
      )
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      customerChan.close()
    }
  }
}

function* customerSubWatcher() {
  // B51: was a single sequential slot (take sub -> fork -> take unsub ->
  // cancel), so only ONE customer could be subscribed to at a time across the
  // WHOLE app. Home.tsx's search-by-ID feature and Customer.tsx's detail
  // screen both dispatch subToSingleCustomer independently, and React
  // Navigation keeps prior screens mounted under the stack - so subscribing
  // to a customer from Home then navigating into a DIFFERENT customer's
  // detail screen silently dropped the detail screen's subscription (the
  // watcher was still parked waiting for Home's unSub), leaving that
  // customer's Redux data frozen at whatever was last cached (e.g. a stale
  // payment_is_applied: true surviving app relaunch via persisted state).
  // Fixed: track one task per customerID so concurrent subscriptions from
  // different screens don't clobber each other.
  const tasks: Record<string, SagaTask> = {}

  while (true) {
    const action: ReturnType<
      typeof subToSingleCustomer | typeof unSubFromSingleCustomer
    > = yield take([subToSingleCustomer, unSubFromSingleCustomer])

    const {
      payload: { customerID },
    } = action

    if (action.type === subToSingleCustomer.type) {
      if (tasks[customerID]) continue

      tasks[customerID] = yield fork(subToCustomerSaga, customerID)
    } else if (tasks[customerID]) {
      yield cancel(tasks[customerID])
      delete tasks[customerID]
    }
  }
}
//#endregion singleCustomer

// function* updateTouchedCostSaga(
//   payload: PayloadAction<{ customerID: string; data: Partial<Customer> }>,
// ) {
//   const db = getDatabase()

//   const customerID = payload.payload.customerID
//   const data = payload.payload.data

//   const customer: Customer =
//     (yield appSelect((state: c.State) => state.customers.data[customerID])) ||
//     schema.createEmptyCustomer({
//       firebaseKey: customerID,
//     })

//   const _hasTouchedCustomerCost = customer.has_touched_customer_cost
//   const _hasTouchedCost = customer.has_touched_cost

//   const hasTouchedCustomerCost = {} as Writable<Partial<schema.TouchedCost>>
//   const hasTouchedCost = {} as Writable<Partial<schema.TouchedCost>>

//   for (const [field] of entries(data)) {
//     const isCustomerCost = field.endsWith('_customer_cost')
//     const isCost = field.endsWith('_cost') && !isCustomerCost
//     if (isCustomerCost) {
//       const parentField = field.replace(
//         '_customer_cost',
//         '',
//       ) as schema.CustomerField

//       // When this write occurs: { parentField: 'no', xxxx_customer_cost: '' }
//       // it is because the efficiency is being reset, therefore the write to
//       // customer cost was not user-initiated thus it should not be set to the
//       // "touched" state
//       hasTouchedCustomerCost[parentField] = data?.[parentField] !== 'no'
//     }
//     if (isCost) {
//       const parentField = field.replace('_cost', '') as schema.CustomerField
//       hasTouchedCost[parentField] = data?.[parentField] !== 'no'
//     }
//   }
//   if (Object.keys(hasTouchedCustomerCost).length) {
//     db.ref('Customers')
//       .child(customerID)
//       .child('has_touched_customer_cost')
//       .set(
//         JSON.stringify({
//           ..._hasTouchedCustomerCost,
//           ...hasTouchedCustomerCost,
//         }),
//       )
//   }
//   if (Object.keys(hasTouchedCost).length) {
//     db.ref('Customers')
//       .child(customerID)
//       .child('has_touched_cost')
//       .set(
//         JSON.stringify({
//           ..._hasTouchedCost,
//           ...hasTouchedCost,
//         }),
//       )
//   }
// }

export function* customersSaga() {
  yield all([
    takeEvery(requestedMoreCustomers, moreCustomersWatcher),
    // takeEvery(updatedCustomerActionCreator, updateTouchedCostSaga),
    call(customersSubWatcher),
    call(customerSubWatcher),
  ])
}
